
import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import { AppContext, User, Post, Product, CartItem, Order, Message, Notification, SubscriptionTier, Analytics } from './context/AppContext';
import { adminUser, buyerUser, mockPosts, mockStories, mockProducts, mockOrders, mockMessages, mockNotifications, subscriptionTiers, mockAnalytics } from './data/mockData';
import Header from './components/Header/Header';
import BottomNav from './components/BottomNav/BottomNav';
import HomePage from './features/Home/HomePage';
import ShopPage from './features/Shop/ShopPage';
import CartPage from './features/Cart/CartPage';
import MessagesPage from './features/Messages/MessagesPage';
import ProfilePage from './features/Profile/ProfilePage';
import ProductDetail from './features/ProductDetail/ProductDetail';
import AdminDashboard from './features/Admin/AdminDashboard';
import LandingPage from './features/Landing/LandingPage';
import SignInPage from './features/Auth/SignInPage';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [viewMode, setViewMode] = useState<'dark' | 'light'>('light');
  const [posts, setPosts] = useState<Post[]>([...mockStories, ...mockPosts]);
  const [products, setProducts] = useState<Product[]>(mockProducts);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>(mockOrders);
  const [messages, setMessages] = useState<Message[]>(mockMessages);
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [currentUser, setCurrentUser] = useState<User>(buyerUser);
  const [analytics] = useState<Analytics>(mockAnalytics);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSelectedProduct(null);
  };

  const handleSignIn = (email: string, password: string, isCreator: boolean) => {
    setCurrentUser(isCreator ? { ...adminUser } : { ...buyerUser, email });
    setIsAdminView(isCreator);
    setIsAuthenticated(true);
    setShowSignIn(false);
  };

  const handleSignOut = () => {
    setIsAuthenticated(false);
    setIsAdminView(false);
    setActiveTab('home');
  };

  const likePost = (postId: string) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const isLiked = p.likedBy.includes(currentUser.id);
        return {
          ...p,
          likes: isLiked ? p.likes - 1 : p.likes + 1,
          likedBy: isLiked ? p.likedBy.filter(id => id !== currentUser.id) : [...p.likedBy, currentUser.id]
        };
      }
      return p;
    }));
  };

  const savePost = (postId: string) => {
    setPosts(prev => prev.map(p => {
      if (p.id === postId) {
        const isSaved = p.savedBy.includes(currentUser.id);
        return {
          ...p,
          savedBy: isSaved ? p.savedBy.filter(id => id !== currentUser.id) : [...p.savedBy, currentUser.id]
        };
      }
      return p;
    }));
  };

  const tipCreator = (amount: number) => {
    const newNotif: Notification = {
      id: Date.now().toString(),
      type: 'tip',
      title: 'Tip Sent!',
      message: `You sent $${amount} tip`,
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  const sendMessage = (content: string, price?: number) => {
    const newMsg: Message = {
      id: Date.now().toString(),
      fromUserId: currentUser.id,
      toUserId: 'admin',
      content,
      timestamp: new Date(),
      read: false,
      price
    };
    setMessages(prev => [...prev, newMsg]);
  };

  const addToCart = (productId: string, size: string, quantity: number) => {
    setCart(prev => {
      const existing = prev.find(item => item.productId === productId && item.size === size);
      if (existing) {
        return prev.map(item => 
          item.productId === productId && item.size === size
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { productId, size, quantity, addedAt: new Date() }];
    });
  };

  const removeFromCart = (productId: string, size: string) => {
    setCart(prev => prev.filter(item => !(item.productId === productId && item.size === size)));
  };

  const updateCartQuantity = (productId: string, size: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }
    setCart(prev => prev.map(item =>
      item.productId === productId && item.size === size ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (productId: string) => {
    setCurrentUser(prev => ({
      ...prev,
      wishlist: prev.wishlist.includes(productId)
        ? prev.wishlist.filter(id => id !== productId)
        : [...prev.wishlist, productId]
    }));
  };

  const placeOrder = (shippingAddressId: string) => {
    const address = currentUser.shippingAddresses.find(a => a.id === shippingAddressId);
    if (!address) return;
    
    const items = cart.map(item => {
      const product = products.find(p => p.id === item.productId)!;
      return {
        productId: item.productId,
        productName: product.name,
        productImage: product.images[0],
        size: item.size,
        quantity: item.quantity,
        price: product.salePrice || product.price
      };
    });
    
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 9.99;
    const tax = subtotal * 0.08;
    
    const newOrder: Order = {
      id: `ord${Date.now()}`,
      userId: currentUser.id,
      items,
      subtotal,
      shipping,
      tax,
      total: subtotal + shipping + tax,
      status: 'pending',
      shippingAddress: address,
      paypalTransactionId: `PP-${Date.now()}`,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    setOrders(prev => [newOrder, ...prev]);
    clearCart();
  };

  const updateOrderStatus = (orderId: string, status: Order['status']) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status, updatedAt: new Date() } : o));
  };

  const subscribe = (tierId: string) => {
    setCurrentUser(prev => ({
      ...prev,
      isSubscribed: true,
      subscriptionTier: tierId,
      subscriptionExpiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    }));
  };

  const unlockContent = (postId: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, isLocked: false } : p));
  };

  const addProduct = (product: Omit<Product, 'id' | 'createdAt' | 'sold' | 'rating' | 'reviews'>) => {
    const newProduct: Product = {
      ...product,
      id: `prod${Date.now()}`,
      createdAt: new Date(),
      sold: 0,
      rating: 0,
      reviews: 0
    };
    setProducts(prev => [...prev, newProduct]);
  };

  const updateProduct = (productId: string, updates: Partial<Product>) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updates } : p));
  };

  const deleteProduct = (productId: string) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
  };

  const createPost = (post: Omit<Post, 'id' | 'likes' | 'comments' | 'tips' | 'likedBy' | 'savedBy' | 'timestamp'>) => {
    const newPost: Post = {
      ...post,
      id: `p${Date.now()}`,
      likes: 0,
      comments: 0,
      tips: 0,
      likedBy: [],
      savedBy: [],
      timestamp: new Date()
    };
    setPosts(prev => [newPost, ...prev]);
  };

  const deletePost = (postId: string) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const updateProfile = (updates: Partial<User>) => {
    setCurrentUser(prev => ({ ...prev, ...updates }));
  };

  const addShippingAddress = (address: Omit<User['shippingAddresses'][0], 'id'>) => {
    const newAddress = { ...address, id: `addr${Date.now()}` };
    setCurrentUser(prev => ({
      ...prev,
      shippingAddresses: [...prev.shippingAddresses, newAddress]
    }));
  };

  const requestPayout = (amount: number) => {
    setCurrentUser(prev => ({
      ...prev,
      availableBalance: prev.availableBalance - amount,
      pendingBalance: prev.pendingBalance + amount
    }));
  };

  const markNotificationRead = (notificationId: string) => {
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const product = products.find(p => p.id === item.productId);
      if (!product) return sum;
      return sum + (product.salePrice || product.price) * item.quantity;
    }, 0);
  }, [cart, products]);

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const renderPage = () => {
    if (selectedProduct && activeTab === 'shop') {
      return <ProductDetail />;
    }
    
    if (isAdminView && activeTab === 'profile') {
      return <AdminDashboard />;
    }

    switch (activeTab) {
      case 'home': return <HomePage />;
      case 'shop': return <ShopPage />;
      case 'cart': return <CartPage />;
      case 'messages': return <MessagesPage />;
      case 'profile': return <ProfilePage onSignOut={handleSignOut} />;
      default: return <HomePage />;
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const unreadMessages = messages.filter(m => !m.read && m.toUserId === currentUser.id).length;

  // Show landing page if not authenticated
  if (!isAuthenticated && !showSignIn) {
    return <LandingPage onGetStarted={() => setShowSignIn(true)} />;
  }

  // Show sign in page
  if (!isAuthenticated && showSignIn) {
    return <SignInPage onSignIn={handleSignIn} onBack={() => setShowSignIn(false)} />;
  }

  const navItems = [
    { id: 'home', label: 'Home', icon: 'home' },
    { id: 'shop', label: 'Shop', icon: 'shop' },
    { id: 'cart', label: 'Cart', icon: 'cart', badge: cartCount },
    { id: 'messages', label: 'Messages', icon: 'messages', badge: unreadMessages },
    { id: 'profile', label: 'Profile', icon: 'profile' }
  ];

  return (
    <AppContext.Provider value={{
      currentUser: isAdminView ? adminUser : currentUser,
      adminUser,
      posts,
      products,
      cart,
      orders,
      messages,
      notifications,
      subscriptionTiers,
      analytics,
      activeTab,
      setActiveTab: handleTabChange,
      selectedProduct,
      setSelectedProduct,
      viewMode,
      setViewMode,
      likePost,
      savePost,
      tipCreator,
      sendMessage,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      toggleWishlist,
      placeOrder,
      updateOrderStatus,
      subscribe,
      unlockContent,
      addProduct,
      updateProduct,
      deleteProduct,
      createPost,
      deletePost,
      updateProfile,
      addShippingAddress,
      requestPayout,
      markNotificationRead,
      cartTotal,
      cartCount,
      isAdminView
    }}>
      <div className="app-container">
        {/* Desktop Sidebar */}
        <aside className="desktop-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <img src="https://jalenward.com/images/logosvg.svg" alt="OnlyJalen" style={{ height: '40px', objectFit: 'contain' }} />
            </div>
          </div>
          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleTabChange(item.id)}
              >
                {item.icon === 'home' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  </svg>
                )}
                {item.icon === 'shop' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6" stroke={activeTab === item.id ? 'white' : 'currentColor'} strokeWidth="2"/>
                    <path d="M16 10a4 4 0 01-8 0" stroke={activeTab === item.id ? 'white' : 'currentColor'} strokeWidth="2" fill="none"/>
                  </svg>
                )}
                {item.icon === 'cart' && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={activeTab === item.id ? 2.5 : 2}>
                    <circle cx="9" cy="21" r="1"/>
                    <circle cx="20" cy="21" r="1"/>
                    <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
                  </svg>
                )}
                {item.icon === 'messages' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                )}
                {item.icon === 'profile' && (
                  <svg viewBox="0 0 24 24" fill={activeTab === item.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={activeTab === item.id ? 0 : 2}>
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
                {item.label}
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>
                )}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div className="sidebar-profile" onClick={() => handleTabChange('profile')}>
              <img src={isAdminView ? adminUser.avatar : currentUser.avatar} alt="Profile" />
              <div className="sidebar-profile-info">
                <span className="sidebar-profile-name">{isAdminView ? adminUser.displayName : currentUser.displayName}</span>
                <span className="sidebar-profile-role">{isAdminView ? 'Creator' : 'Subscriber'}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main App */}
        <div className="app-main-wrapper">
          <div className={`app light-mode`}>
            <Header 
              isAdminView={isAdminView} 
              setIsAdminView={setIsAdminView}
              unreadNotifications={unreadNotifications}
            />
            <main className="app-main">
              {renderPage()}
            </main>
            <div className="mobile-bottom-nav">
              <BottomNav 
                cartCount={cartCount}
                unreadMessages={unreadMessages}
              />
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <aside className="desktop-right-sidebar">
          <div className="right-sidebar-card">
            <h3>🔥 Trending</h3>
            {[
              { title: 'New Collection Drop', desc: '2.5K views' },
              { title: 'Behind the Scenes', desc: '1.8K views' },
              { title: 'Exclusive Q&A', desc: '1.2K views' }
            ].map((item, i) => (
              <div key={i} className="trending-item">
                <span className="trending-number">#{i + 1}</span>
                <div className="trending-info">
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="right-sidebar-card">
            <h3>📊 Quick Stats</h3>
            <div style={{ fontSize: '14px', color: 'var(--light-text-secondary)', lineHeight: '1.8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Posts</span>
                <strong style={{ color: 'var(--light-text)' }}>{posts.filter(p => !p.isStory).length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Stories Active</span>
                <strong style={{ color: 'var(--light-text)' }}>{posts.filter(p => p.isStory).length}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Products</span>
                <strong style={{ color: 'var(--light-text)' }}>{products.length}</strong>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </AppContext.Provider>
  );
}

export default App;
